import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isEqual } from 'lodash';
import { constants } from '../../../constants/configuration';
import {
  customErrorCodes,
  exceptionComponentPageTypes,
  apiResponseCodes,
  toastMessageTime,
} from '../../../constants/globals';
import asyncRoute from '../../../helpers/hoc/asyncRoute';
import routes from '../../../routes';
import {
  fetchViewCartItemsAction,
  fetchAvailableOffersAction,
  beginAjaxCallAction,
  endAjaxCallAction,
} from '../../../actions/app.action';
import {
  signInByTrueCallerAction,
  dispatchGuestLoginDataAction,
} from '../../../actions/authentication';
import {
  generateUUID,
  getCurrentRestaurantCode,
  isIOS,
  isUserLoggedIn,
  toTitleCase,
} from '../../../utils/helpers';
import {
  selectRestaurant,
  selectMenu,
  getErrorTitle,
  getErrorDescription,
  getButtonHandlerCta,
  getTrueCallerDeepLink,
  getTrueCallerErrorContent,
} from '../../../selectors';
import './OfferList.scss';

const Header = asyncRoute(() => import('../../../components/Header'));
const OfferCard = asyncRoute(() => import('../../../components/Card/OfferCard'));
const AuthenticationPopup = asyncRoute(() =>
  import('../../../components/Popup/AuthenticationPopup'),
);
const OfferDetailsPopup = asyncRoute(() => import('../../../components/Popup/OfferDetailsPopup'));
const ExclusiveOfferCard = asyncRoute(() => import('../../../components/Card/ExclusiveOfferCard'));
const ExceptionComponent = asyncRoute(() => import('../../../components/ExceptionComponent'));
const Toast = asyncRoute(() => import('../../../components/elements/Toast'));

const authenticationConfig = constants.authentication;

class OfferList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showAuthenticationPopup: false,
      showOfferDetailsPopup: false,
      appliedOffers: [],
      offer: {},
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
      errorPopupCancellable: true,
      errorPopupStyleType: exceptionComponentPageTypes.halfSize,
      toast: '',
    };
    this.handleOfferClick = this.handleOfferClick.bind(this);
    this.onCloseOfferDetailsPopup = this.onCloseOfferDetailsPopup.bind(this);
    this.onCloseAuthenticationPopup = this.onCloseAuthenticationPopup.bind(this);
  }

  componentDidMount() {
    let deliveryLocation = {};
    const { viewCartItems, restaurant, selectedTable, selectedAddress, loginData } = this.props;
    const restaurantId = get(restaurant, 'id', '');
    const restaurantTableId = get(selectedTable, 'id', '');
    const guestId = get(loginData, 'id', '');
    const items = get(viewCartItems, 'items', []);
    selectedAddress && delete selectedAddress.id;
    if (isEqual(get(selectedTable, 'type', ''), 'DL')) {
      deliveryLocation.guestAddress = selectedAddress;
    }

    // If viewCartItems app state in store is empty due to contextless hit to offer-list url, show error
    if (isEmpty(this.props.viewCartItems)) {
      this.setState({
        showErrorPopup: true,
        showContactInfoPopup: false,
        errorContent: {
          title: getErrorTitle({ code: customErrorCodes.contextlessURLHit }),
          description: getErrorDescription({ code: customErrorCodes.contextlessURLHit }),
          buttonCta: getButtonHandlerCta({ code: customErrorCodes.contextlessURLHit }),
        },
        handleErrorCTA: this.handleContextlessURLHitErrorPopupCTA,
        errorPopupStyleType: exceptionComponentPageTypes.fullSize,
      });
    }

    const offersReqBody = {
      restaurantId,
      restaurantTableId,
      guestId,
      items,
      deliveryLocation,
      isShowOffers: true,
    };

    this.props.fetchAvailableOffersAction({ offersReqBody });

    // Scroll to top to prevent browser scroll position persistence from other screens
    window.scrollTo({ top: 0 });
  }

  componentDidUpdate(prevProps, prevState) {
    const { toast } = this.state;
    // Generic toast state
    if (!isEmpty(toast) && !isEqual(toast, prevState.toast)) {
      setTimeout(
        () =>
          this.setState({
            toast: '',
          }),
        toastMessageTime,
      );
    }
  }

  componentWillReceiveProps(nextProps) {
    const { viewCartItems } = this.props;
    const { appliedOffers } = this.state;
    if (
      !isEqual(get(this.props, 'loginData'), get(nextProps, 'loginData')) &&
      !isEmpty(get(nextProps, 'loginData'))
    ) {
      this.setState(
        {
          showAuthenticationPopup: false,
        },
        () => {
          const guestId = get(nextProps, 'loginData.id', '');
          this.props
            .fetchViewCartItemsAction({ ...viewCartItems, appliedOffers, guestId })
            .then((response) => {
              if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
                this.props.history.push(`/${routes.containers.cart}`);
              }
            });
        },
      );
    }
  }

  onBackClick = () => {
    this.props.history.push(`/${routes.containers.cart}`);
  };

  handleOfferClick = (offer) => {
    let appliedOffers = [];
    const { viewCartItems, fetchViewCartItemsAction, loginData } = this.props;
    const { id, isLoginRequired, offerSelectableMenu } = offer;
    appliedOffers.push(id);
    if (isLoginRequired) {
      this.setState({
        appliedOffers,
        showAuthenticationPopup: true,
      });
    } else if (!isEmpty(offerSelectableMenu)) {
      //redirect to avail-offer section
      this.props.history.push(`/${routes.containers.offers}/${routes.offers.availOffer}/${id}`);
    } else {
      // logic for logged in condition
      const guestId = get(loginData, 'id', '');
      fetchViewCartItemsAction({ ...viewCartItems, appliedOffers, guestId }).then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
          this.props.history.push(`/${routes.containers.cart}`);
        }
      });
    }
  };

  handleViewOfferDetailsClick = (offer) => {
    this.setState({
      showOfferDetailsPopup: true,
      offer,
    });
  };

  handleTrueCallerSignIn = ({ requestId }) => {
    this.props.beginAjaxCallAction();
    this.props.signInByTrueCallerAction({ requestId }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
        const guestLoginData = get(response, 'data', {});
        this.setState(
          {
            toast: constants.authentication.toastMessage.successfulLogin,
          },
          () => {
            this.props.dispatchGuestLoginDataAction(guestLoginData);
            this.props.endAjaxCallAction();
          },
        );
      } else {
        const code = get(response, 'data.code', '');
        const message = toTitleCase(get(response, 'data.message', ''));
        if (
          isEqual(code, authenticationConfig.userRejected) ||
          isEqual(code, authenticationConfig.trueCallerTimeout) ||
          isEqual(code, authenticationConfig.trueCallerRequestError)
        ) {
          let errorContent = getTrueCallerErrorContent({
            code,
            message,
          });
          this.setState(
            {
              showErrorPopup: true,
              errorContent,
              errorCode: code,
              handleErrorCTA: this.handleTrueCallerExceptionCTA,
            },
            () => {
              this.props.endAjaxCallAction();
            },
          );
        } else if (isEqual(code, authenticationConfig.requestIdNotFound)) {
          // Again hit the true caller sing in handler
          this.handleTrueCallerSignIn({ requestId });
        } else {
          this.setState(
            {
              showAuthenticationPopup: true,
            },
            () => {
              this.props.endAjaxCallAction();
            },
          );
        }
      }
    });
  };

  handleLoginClick = () => {
    const { appSettings, restaurantConfigAdditionalProps } = this.props;
    // first check at restaurant level then check at app level
    const isTrueCallerEnabled = get(
      restaurantConfigAdditionalProps.find((prop) => get(prop, 'code') === 'tc.isEnabled'),
      'value',
      get(appSettings, 'tc.isEnabled', 'false'),
    );
    if (JSON.parse(isTrueCallerEnabled) && !isIOS()) {
      const requestId = generateUUID();
      const trueCallerDeeplink = getTrueCallerDeepLink({ requestId });
      window.location = trueCallerDeeplink;
      setTimeout(() => {
        if (document.hasFocus()) {
          // Truecaller app not present on the device and you redirect the user the regular otp flow
          this.setState({
            showAuthenticationPopup: true,
          });
        } else {
          // Truecaller app present on the device and the profile overlay opens
          this.handleTrueCallerSignIn({ requestId });
        }
      }, 600);
    } else {
      this.setState({
        showAuthenticationPopup: true,
      });
    }
  };

  onCloseAuthenticationPopup = () => {
    this.setState({
      showAuthenticationPopup: false,
    });
  };

  onCloseOfferDetailsPopup = () => {
    this.setState({
      showOfferDetailsPopup: false,
      offer: {},
    });
  };

  handleCta = () => {
    const { selectedTable, restaurant, clearViewCartItems } = this.props;
    const restaurantCode = get(restaurant, 'restaurantCode', '');
    const tableNumber = get(selectedTable, 'tableNumber', '');
    let currentRestaurantCode = getCurrentRestaurantCode({ restaurantCode, tableNumber });
    this.setState(
      {
        showErrorPopup: false,
      },
      () => {
        clearViewCartItems().then(() => {
          this.props.history.push(currentRestaurantCode);
        });
      },
    );
  };

  onCloseExceptionComponent = () => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
    });
  };

  handleContextlessURLHitErrorPopupCTA = () => {
    // Reload and open webapp's home
    window.location.replace(window.location.origin);
  };

  handleTrueCallerExceptionCTA = () => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
    });
    this.handleLoginClick();
  };

  render() {
    const { availableOffers } = this.props;
    const {
      showAuthenticationPopup,
      showOfferDetailsPopup,
      offer,
      showErrorPopup,
      errorContent,
      handleErrorCTA,
      errorPopupCancellable,
      errorPopupStyleType,
      toast,
    } = this.state;

    const sortedAvailableOffer = [
      ...availableOffers.filter((offer) => get(offer, 'isGuestSpecific')),
      ...availableOffers.filter((offer) => !get(offer, 'isGuestSpecific')),
    ];
    return (
      <>
        <Header onBackClick={this.onBackClick} identifier={routes.containers.offers} />
        <div className="offer-list" id="offer-list-wrapper">
          {!isEmpty(toast) && <Toast content={toast} />}
          {!isUserLoggedIn() && (
            <ExclusiveOfferCard
              CTAText={constants.offers.loginCTA}
              onClick={this.handleLoginClick}
            />
          )}
          {sortedAvailableOffer &&
            sortedAvailableOffer.map((offer, index) => (
              <OfferCard
                orientation="vertical"
                key={index}
                offer={offer}
                isOfferApplicable
                handleOfferClick={(offer) => this.handleOfferClick(offer)}
                handleViewOfferDetailsClick={(offer) => this.handleViewOfferDetailsClick(offer)}
              />
            ))}
        </div>
        {showOfferDetailsPopup && (
          <OfferDetailsPopup
            openSegmentPopup={showOfferDetailsPopup}
            dismissSegmentPopup={this.onCloseOfferDetailsPopup}
            handleOfferClick={(offer) => this.handleOfferClick(offer)}
            isOfferApplicable
            offer={offer}
          />
        )}
        {showAuthenticationPopup && (
          <AuthenticationPopup
            openSegmentPopup={showAuthenticationPopup}
            dismissSegmentPopup={this.onCloseAuthenticationPopup}
          />
        )}
        {showErrorPopup && (
          <ExceptionComponent
            pageType={errorPopupStyleType}
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={errorPopupCancellable ? this.onCloseExceptionComponent : undefined} // Sending this prop as undefined as the SegmentComponent being used internally in ExceptionComponent implicitly handles it
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={handleErrorCTA}
          />
        )}
      </>
    );
  }
}

export default connect(
  (state) => ({
    appSettings: get(state, 'app.masterData.appSettings', {}),
    menu: selectMenu(state),
    restaurant: selectRestaurant(state),
    viewCartItems: get(state, 'app.viewCart', {}),
    selectedTable: get(state, 'app.selectedTable', {}),
    loginData: get(state, 'authentication.loginData', {}),
    restaurantConfig: get(state, 'app.restaurant.restaurantConfig', {}),
    selectedAddress: get(state, 'app.selectedAddress', {}),
    availableOffers: get(state, 'app.availableOffers', []),
    restaurantConfigAdditionalProps: get(
      state,
      'app.restaurant.restaurantConfig.additionalProperties',
      [],
    ),
  }),
  {
    fetchViewCartItemsAction,
    fetchAvailableOffersAction,
    signInByTrueCallerAction,
    dispatchGuestLoginDataAction,
    beginAjaxCallAction,
    endAjaxCallAction,
  },
)(OfferList);
